import { useEffect, useState } from "react";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { AddScriptDialog } from "@/components/community/admin/seo/AddScriptDialog";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useIsMobile } from "@/hooks/use-mobile";

interface ExternalScript {
  id: string;
  name: string;
  script_type: 'GA' | 'GTM' | 'META_PIXEL';
  tracking_id: string;
  is_active: boolean;
  created_at: string;
}

const scriptTypeLabels = { GA: 'Google Analytics 4', GTM: 'Google Tag Manager', META_PIXEL: 'Meta Pixel' };

export default function SEOScripts() {
  usePageMeta({ title: "Scripts SEO - Data Hire Club", description: "Gerenciamento de scripts de tracking", noindex: true });

  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [scripts, setScripts] = useState<ExternalScript[]>([]);
  const [isAddScriptOpen, setIsAddScriptOpen] = useState(false);

  useEffect(() => { fetchScripts(); }, []);

  const fetchScripts = async () => {
    const { data, error } = await supabase.from('external_scripts').select('*').order('created_at', { ascending: false });
    if (error) { toast({ title: "Erro ao carregar scripts", description: error.message, variant: "destructive" }); return; }
    setScripts(data?.filter(script => ['GA', 'GTM', 'META_PIXEL'].includes(script.script_type)) as ExternalScript[] || []);
  };

  const toggleScriptStatus = async (id: string, currentStatus: boolean) => {
    const { error } = await supabase.from('external_scripts').update({ is_active: !currentStatus }).eq('id', id);
    if (error) { toast({ title: "Erro ao atualizar status", description: error.message, variant: "destructive" }); return; }
    fetchScripts();
    toast({ title: "Status atualizado", description: "O status do script foi atualizado com sucesso." });
  };

  return (
    <CandidateLayout maxWidth="5xl">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-lg sm:text-xl">Traqueamento</CardTitle>
          <Button className="bg-primary hover:bg-primary/90 w-full sm:w-auto" onClick={() => setIsAddScriptOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Script
          </Button>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            // Mobile: Cards view
            <div className="space-y-3">
              {scripts.map((script) => (
                <Card key={script.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{script.name}</p>
                      <p className="text-xs text-muted-foreground">{scriptTypeLabels[script.script_type]}</p>
                    </div>
                    <Switch
                      checked={script.is_active}
                      onCheckedChange={() => toggleScriptStatus(script.id, script.is_active)}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate flex-1">{script.tracking_id}</span>
                    <Badge variant={script.is_active ? "default" : "secondary"} className="text-xs ml-2">
                      {script.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </Card>
              ))}
              {scripts.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Nenhum script configurado</p>
              )}
            </div>
          ) : (
            // Desktop: Table view
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>ID de Rastreamento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scripts.map((script) => (
                    <TableRow key={script.id}>
                      <TableCell>{script.name}</TableCell>
                      <TableCell>{scriptTypeLabels[script.script_type]}</TableCell>
                      <TableCell>{script.tracking_id}</TableCell>
                      <TableCell>
                        <Switch
                          checked={script.is_active}
                          onCheckedChange={() => toggleScriptStatus(script.id, script.is_active)}
                        />
                      </TableCell>
                      <TableCell>{new Date(script.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <AddScriptDialog open={isAddScriptOpen} onOpenChange={setIsAddScriptOpen} onSuccess={fetchScripts} />
    </CandidateLayout>
  );
}
